import { useQueryClient } from "@tanstack/react-query";
import { BellIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Notification } from "./ui/Notification";

import { useGetNotifications, useMarkAllNotificationsAsRead } from "@/common/api";
import type { NotificationDTO } from "@/common/api/sdk";
import { LoadingModal, TablePagination } from "@/common/components";
import { Card, CardContent } from "@/common/components/ui/card";
import { useUser } from "@/common/hooks";

const PAGE_SIZE = 10;

export function NotificationsPage() {
  const { user } = useUser();
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const markAllAsReadMutation = useMarkAllNotificationsAsRead({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["/api/notification/count"] });
      },
    },
  });

  const {
    data: notificationsData,
    isLoading,
    isSuccess,
  } = useGetNotifications(
    {
      userId: user?.id ?? "",
      page,
      limit: PAGE_SIZE,
    },
    {
      query: {
        enabled: !!user?.id,
        refetchInterval: 5_000,
      },
    },
  );

  const notifications: NotificationDTO[] = useMemo(
    () => notificationsData?.data?.items ?? [],
    [notificationsData?.data?.items],
  );

  const totalPages = notificationsData?.data?.totalPage ?? 0;
  const totalItems = notificationsData?.data?.totalItems ?? 0;

  useEffect(() => {
    if (isSuccess && page === 1 && user?.id) {
      markAllAsReadMutation.mutate({ params: { userId: user.id } });
    }
  }, [page, user?.id, isSuccess]);

  if (isLoading) {
    return <LoadingModal isOpen={true} />;
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold color-primary">Уведомления</h2>
        <p className="text-lg font-medium text-gray-700 mt-2">Все важные уведомления системы</p>
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent>
            <div className="text-center">
              <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="caption">Уведомлений пока нет</p>
              <p className="text-sm text-gray-500 mt-1">
                Здесь будут отображаться важные уведомления системы
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-7">
            {notifications.map((notification, index) => (
              <Notification key={notification.id ?? `notification-${index}`} data={notification} />
            ))}
          </div>
          <div className="mt-6">
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </>
  );
}
